import { Component, inject } from '@angular/core';
import { MessageService } from 'primeng/api';
import { Message } from 'primeng/message';
import { ApiResponse } from '../../models/response.model';
import { Subscription } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { DateHelper, HttpStatusCode } from '@template-ui/helpers';
import { ToastModule } from 'primeng/toast';
import { ProgressBarModule } from 'primeng/progressbar';
import { CommonModule } from '@angular/common';
import { UiLibraryModule } from "@template-ui/template-ui.module";
import { AppBaseComponent } from "../app-base.component";

type toastPosition = "top-left" | "top-center" | "top-right" | "bottom-left" | "bottom-center" | "bottom-right" | "center";

/**
 * This component exists to provide a way to keep custom toasts together and let the developer
 * specify the toast to use. The simplest way to get a PrimeNG toast message is to do the following:
 * ```
 * @Component({
 *   ...
 * //  providers: [MessageService]  <-- NOT! MessageService is provided in main.ts. DON'T ADD IT ANYWHERE ELSE
 * })
 *
 * pngMessages = inject(MessageService);
 *
 * someMethod()
 * {
 *    // show toast default
 *    this.pngMessages.add({ severity: 'info', summary: 'Info', detail: 'Message Content', life: 3000 });
 *
 *
 *    // show custom toast
 *    this.pngMessages.add(
 *
 *
 * }
 */
@Component({
    selector: 'app-custom-prime-toast',
    templateUrl: './custom-prime-toast.component.html',
    styleUrls: ['./custom-prime-toast.component.scss'],
    imports: [
        CommonModule,
        ToastModule, ProgressBarModule,
        UiLibraryModule
    ]
})
export class CustomPrimeToastComponent extends AppBaseComponent {
  constructor() {
    super();
    this.thisClassName = 'CustomPrimeToastComponent';
  }


  apiErrorResponse?: ApiResponse<any>;
  httpError?: HttpErrorResponse;
  httpErrorInfo?: HttpStatusCode;
  apiErrorShowDetails:boolean = false;

  // Save the timeout so it can be cancelled
  progressTimeoutId?:any;

  progressStatus: "new" | "progressing" = "new";

  showProgressBar: boolean = true;

  progress: number = 101;

  visible: boolean = true;

  messageService = inject(MessageService);

  //constructor(private messageService: MessageService) { }

  onApiErrorDetailsClick(event: any, messageData: any)
  {
    event.preventDefault();

    this.dlog("Details clicked");
    this.resetApiError('api-error');
    this.messageService.clear();

    if(messageData.apiError)
    {

      // Save the response for easy access in the template
      this.apiErrorResponse = messageData.apiError;

      this.messageService.add({
        key: "api-error-details",
        severity: 'error',
        summary:'API Error Details',
        detail: messageData.apiError.message,
        sticky: true,  // Keep it up until dismissed
        closable: false // close button is implemented in the template
        //data : apiErrorResponse as ApiResponse<any>,
      });
    }

    if(messageData.httpError)
    {
      // Save the response for easy access in the template
      this.httpError = messageData.httpError;
      //this.httpErrorInfo = getHttpStatusCodeInfo(this.httpError?.status);

      this.messageService.add({
        key: "http-error-details",
        severity: 'error',
        summary:'HTTP Error Details',
        detail: messageData.httpError.message,
        sticky: true,  // Keep it up until dismissed
        closable: false // close button is implemented in the template
        //data : apiErrorResponse as ApiResponse<any>,
      });
    }
  }

  resetApiError(key: string)
  {

    // Clear the existing timeout in case the user closed the toast and its still going.
    clearTimeout(this.progressTimeoutId);
    //this.dlog(`timeout cleared`);

    this.messageService.clear(key);  //  Clear the dialog

    setTimeout(() => {
      this.progressStatus = "new";
      this.progress = 100;
      this.showProgressBar = true;  // for next time.
      //this.dlog(`Reset progress status.`);
    }, 200);  // Add a little time in case the template calls this again before it completely goes away.
  }

  startTime = Date.now();
  remainingTime:number = 0;
  getProgress(message: Message): number
  {
    const time = DateHelper.formatDateWithDefaults(new Date(), "hh:mm:ss.SSS");

    //this.dlog(`${time} getProgress called. progressStatus = ${this.progressStatus}`);

    switch(this.progressStatus)
    {
      case "new":
        // First time this is called for a new toast
        // Set progress to 100% so it shows up
        clearTimeout(this.progressTimeoutId); // just in case it didn't get clear earlier
        this.progress = 100;
        this.showProgressBar = true;
        // Set the animation duration to be the same as the lifetime of the toast
        document.documentElement.style.setProperty('--api-error-animation-duration', `${message.life}ms`);

        // After a short time, set progress to 0 so the animation can start
        setTimeout(() => {
          this.progress = 0;
          this.progressStatus = "progressing";
        }, 200);
        // After the toast times out, reset everything for the next one
        this.startTime = Date.now();
        this.remainingTime = message.life || 0;

        this.progressTimeoutId = setTimeout(() => {

          if(this.showProgressBar)
          {
            this.resetApiError('api-error');
            this.messageService.clear();
          }
        }, message.life);  // Add a little time in case the template calls this again before it completely goes away.

        break;

      case "progressing":
        // Nothing to do here, the animation is going
        break;
    }

    return this.progress;
  }


  stopTimeout()
  {
    clearTimeout(this.progressTimeoutId);
    this.showProgressBar = false;
  }

  onToastClicked(event: Event)
  {
    this.dlog('Toast clicked!');
    this.stopTimeout();
  }

  onClose(key?: string)
  {
    this.dlog(`onClose called. Key=${key}`);
    this.messageService.clear(key);

    this.resetApiError(key || '');

  }


  private clickSubscriptionForCloseOutside?: Subscription;

  ngOnInit(): void {

    // This lets the user close the toast by clicking anywhere outside the toast
    // TODO: But it keeps firing long after all toasts are gone. Figure out how to unsubscribe? Maybe? Is it a big deal?

    /* This worked in BITS2 (although not used?)
      It does not appear to be working here because this gets called immediately
      so the effect os that the toast is never seen.#20310
      TODO: If we want a toast that can be dismissed by clicking outside, fix this. Otherwise just leave it for later.#20310
    */
    // this.clickSubscriptionForCloseOutside = fromEvent(document, 'click').subscribe(event => {
    //   const target = event.target as HTMLElement;
    //   if (!target.closest('.p-toast')) {
    //     console.warn(`Clearing toast due to outside click`);
    //     //this.messageService.clear(); // <-- happens too soon in BITS3
    //     this.resetApiError('api-error');
    //   }
    // });
  }

  // ngOnDestroy() {
  //   // Never happens cuz this is never destroyed.
  //   this.clickSubscriptionForCloseOutside?.unsubscribe();
  // }

}
